import React, { useState, useMemo } from "react";
import {
  Avatar,
  Box,
  Badge,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQueries } from "@tanstack/react-query";
import { format } from "date-fns";
import { MobiliAvatar } from "../../../atoms/MobiliAvatar";
import { getPriorityColor } from "../../Ticketing/TicketOverview";
import { useTickets } from "../../../stores/Ticketing/ticket.queries";
import { FindAllTicketsQueryFiltersTicketStatusEnum, TicketDto } from "../../../api-client";
import { useApi } from "../../../api/ApiProvider";
import { rxKeys } from "../../../stores/Rx/rx.keys";
import TicketDialog from "../../Ticketing/TicketDialog";

interface ClinicTicketsSectionProps {
  clinicId: string;
}

const ClinicTicketsSection: React.FC<ClinicTicketsSectionProps> = ({ clinicId }) => {
  const [selectedTicketId, setSelectedTicketId] = useState<number | undefined>();
  const [dialogOpen, setDialogOpen] = useState(false);
  const { rx: rxApi } = useApi();

  const openStatuses = Object.values(FindAllTicketsQueryFiltersTicketStatusEnum).filter(
    (s) => s !== FindAllTicketsQueryFiltersTicketStatusEnum.Closed
  );

  const filters = useMemo(
    () => ({ limit: 100, ticketStatus: openStatuses }),
    []
  );

  const { data: ticketsData, isLoading } = useTickets({
    filters,
    enabled: !!clinicId,
  });

  // Get all tickets with rxIds
  const ticketsWithRx = useMemo(() => {
    if (!ticketsData?.pages) return [];
    return ticketsData.pages.flat().filter((t) => !!t.rxId);
  }, [ticketsData]);

  // Batch-fetch full Rx data for each ticket's rxId to get clinicId
  const rxQueries = useQueries({
    queries: ticketsWithRx.map((ticket) => ({
      queryKey: rxKeys.rx.detail(ticket.rxId!),
      queryFn: () => rxApi.getRxWithValidations(ticket.rxId!),
      enabled: !!ticket.rxId,
      staleTime: 2 * 60 * 1000,
    })),
  });

  // Filter tickets whose Rx belongs to this clinic
  const allTickets: TicketDto[] = useMemo(() => {
    return ticketsWithRx.filter((ticket, index) => {
      const rxData = rxQueries[index]?.data as any;
      if (!rxData) return false;
      return rxData.clinicId === clinicId || rxData.clinic?.id === clinicId;
    });
  }, [ticketsWithRx, rxQueries, clinicId]);

  const rxQueriesLoading = rxQueries.some((q) => q.isLoading);

  const handleRowClick = (ticket: TicketDto) => {
    setSelectedTicketId(ticket.id);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTicketId(undefined);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography variant="h6" sx={{ fontSize: "1rem", fontWeight: 600 }}>
          Offene Tickets
        </Typography>
        <Badge
          badgeContent={allTickets.length}
          color="error"
          sx={{ ml: 1 }}
        />
      </Box>

      {isLoading || rxQueriesLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : allTickets.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
          Keine offenen Tickets
        </Typography>
      ) : (
        <TableContainer sx={{ maxHeight: 250, overflow: "auto" }}>
          <Table size="small" stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell align="center">ID#</TableCell>
                <TableCell align="center">Typ</TableCell>
                <TableCell align="center">Datum</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Priorität</TableCell>
                <TableCell align="center">Zugewiesen</TableCell>
                <TableCell align="center">Betroffen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allTickets.map((t, i) => (
                <TableRow
                  hover
                  key={`clinic-ticket-${t.id}-${i}`}
                  onClick={() => handleRowClick(t)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell align="center">
                    <Chip size="small" label={t.id} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={t.ticketType || "-"} />
                  </TableCell>
                  <TableCell align="center">
                    {t.createdAt ? format(new Date(t.createdAt as string), "dd.MM.yyyy") : "-"}
                  </TableCell>
                  <TableCell align="center">
                    <Chip size="small" label={t.ticketStatus} />
                  </TableCell>
                  <TableCell align="center">
                    <Avatar
                      sx={{
                        width: 22,
                        height: 22,
                        bgcolor: t.priority && getPriorityColor(t.priority),
                        margin: "auto",
                      }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <MobiliAvatar user={t.assignedToUser} sx={{ width: 26, height: 26, margin: "auto" }} />
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      size="small"
                      label={t.patient ? `${t.patient.firstName} ${t.patient.lastName}` : "-"}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <TicketDialog
        open={dialogOpen}
        handleClose={handleCloseDialog}
        ticketId={selectedTicketId}
      />
    </Box>
  );
};

export default ClinicTicketsSection;
